// @ts-nocheck
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn, UserPlus, User as UserIcon, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Mode = 'login' | 'signup';

/**
 * Tela de login/cadastro mobile-first usada no PWA.
 * - Sem header/BottomNav do MobileLayout (essa rota é renderizada "fora" do layout).
 * - Login com Google redireciona DE VOLTA para /pwa/dashboard (evita cair no SPA desktop).
 */
export default function MobileAuth() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Destino após login (usado quando veio de RequireAuth com state.from)
  const redirectTarget: string =
    (location.state as any)?.from || '/pwa/dashboard';

  // Se já está logado, cai direto no destino.
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (mounted && data.session?.user) {
        navigate(redirectTarget, { replace: true });
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        navigate(redirectTarget, { replace: true });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate, redirectTarget]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err: any) {
      toast({
        title: 'Falha no login',
        description: err?.message || 'Email ou senha inválidos.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'Use pelo menos 6 caracteres.',
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/pwa/dashboard`,
          data: { full_name: fullName },
        },
      });
      if (error) throw error;
      toast({
        title: 'Conta criada',
        description: 'Verifique seu email para confirmar.',
      });
      setMode('login');
    } catch (err: any) {
      toast({
        title: 'Falha ao criar conta',
        description: err?.message || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Redireciona de volta para DENTRO do PWA, não para a raiz (que cai no SPA desktop).
          redirectTo: `${window.location.origin}/pwa/dashboard`,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });
      if (error) throw error;
      // Se estiver em standalone PWA, o navegador vai abrir Custom Tab / Safari.
      // O Supabase finaliza o fluxo via URL hash quando voltar.
    } catch (err: any) {
      toast({
        title: 'Falha no login com Google',
        description: err?.message || 'Tente novamente.',
        variant: 'destructive',
      });
      setGoogleLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-primary/10 via-background to-background"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/logoasad.png"
              alt="Allana Sthéfany Advocacia"
              className="w-28 h-28 object-contain rounded-2xl shadow-lg shadow-primary/20 mb-4"
            />
            <h1 className="text-xl font-bold text-foreground text-center">
              Allana Sthéfany
            </h1>
            <p className="text-xs text-muted-foreground text-center">
              Advocacia &amp; Consultoria
            </p>
          </div>

          {/* Tabs Login / Cadastro */}
          <div className="flex rounded-xl bg-muted p-1 mb-5">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg transition ${
                mode === 'login'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground'
              }`}
            >
              <LogIn className="h-4 w-4" />
              Entrar
            </button>
            <button
              type="button"
              onClick={() => setMode('signup')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg transition ${
                mode === 'signup'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground'
              }`}
            >
              <UserPlus className="h-4 w-4" />
              Cadastrar
            </button>
          </div>

          {/* Form */}
          <form
            onSubmit={mode === 'login' ? handleLogin : handleSignup}
            className="space-y-3"
          >
            {mode === 'signup' && (
              <div className="space-y-1.5">
                <Label htmlFor="fullName" className="text-xs">
                  Nome completo
                </Label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Seu nome"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-12 pl-10 text-base"
                    required
                    autoComplete="name"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-10 text-base"
                  required
                  inputMode="email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder={mode === 'signup' ? 'Mínimo 6 caracteres' : 'Sua senha'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 text-base"
                  required
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full h-12 text-base font-semibold mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'login' ? 'Entrando…' : 'Criando conta…'}
                </>
              ) : mode === 'login' ? (
                'Entrar'
              ) : (
                'Criar conta'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
              <span className="bg-background px-2 text-muted-foreground">
                ou continue com
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={loading || googleLoading}
            onClick={handleGoogle}
            className="w-full h-12 text-base font-semibold"
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" className="mr-2 h-5 w-5" aria-hidden>
                <path
                  fill="#EA4335"
                  d="M12 5c1.6 0 3 .5 4.1 1.5l3-3C17.1 1.7 14.7 1 12 1 7.4 1 3.4 3.6 1.4 7.5l3.4 2.7C5.9 7.1 8.7 5 12 5z"
                />
                <path
                  fill="#34A853"
                  d="M23 12.3c0-.9-.1-1.8-.3-2.6H12v5h6.2c-.3 1.4-1.1 2.6-2.3 3.4l3.4 2.7c2-1.8 3.7-4.5 3.7-8.5z"
                />
                <path
                  fill="#4A90E2"
                  d="M4.8 14.2c-.2-.7-.3-1.4-.3-2.2s.1-1.5.3-2.2L1.4 7.1C.5 8.6 0 10.2 0 12s.5 3.4 1.4 4.9l3.4-2.7z"
                />
                <path
                  fill="#FBBC05"
                  d="M12 23c3 0 5.5-1 7.3-2.7l-3.4-2.7c-1 .7-2.3 1.1-3.9 1.1-3.3 0-6.1-2.1-7.2-5.2L1.4 16.2C3.4 20.1 7.4 23 12 23z"
                />
              </svg>
            )}
            Entrar com Google
          </Button>

          <p className="text-center text-[11px] text-muted-foreground mt-6 px-2">
            Ao continuar, você aceita nossos termos de uso e política de privacidade.
          </p>
        </div>
      </div>
    </div>
  );
}
